import { Directive, EventEmitter } from '@angular/core';
import { OrgTreeService } from '@app/main/org-tree/services/org-tree.service';
import { BehaviorSubject } from 'rxjs';
import { IResponsible, ResponsibleType } from '@shared/interfaces';
import { IOrganisationConfiguration } from '@shared/interfaces/responsible/IOrganisationConfiguration';
import { IListView } from '@shared/interfaces/various/IListView';
import { IQueryOrganisation } from '@shared/interfaces/queries/IQueryOrganisation';
import { currentDate, currentReportingPeriod } from '@shared/helpers/helpers';
import { ICommunicationConfiguration } from '@shared/interfaces/responsible/ICommunicationConfiguration';
import moment from 'moment';
import { ModuleDirective } from '@shared/directives/module.directive';
import { ModuleService } from '@shared/services/module.service';

@Directive()
export class OrgTreeActionsDirective extends ModuleDirective {
    activePeriod = currentReportingPeriod;

    constructor(
        public orgTreeService: OrgTreeService,
        moduleService: ModuleService
    ) {
        super(moduleService);
    }

    get isEditActiveSubscription(): BehaviorSubject<boolean> {
        return this.orgTreeService.isEditActive;
    }

    get isEditActive(): boolean {
        return (
            this.orgTreeService.isEditActive.value &&
            this.currentConfiguration &&
            !this.currentConfiguration?.isCascadingDisabled
        );
    }

    set isEditActive(val: boolean) {
        this.orgTreeService.isEditActive.next(val);
        this.orgTreeService.savedIsEditActive = val;
    }

    get isEditDisabled(): boolean {
        return this.currentConfiguration?.isCascadingDisabled || this.rootNode?.isDisabled;
    }

    get savedIsActive(): boolean {
        return this.orgTreeService.savedIsEditActive;
    }

    get isDrawerOpen(): boolean {
        return this.orgTreeService.toggleDrawer.value?.isOpen;
    }

    get toggleDrawerSubscription(): BehaviorSubject<{ isOpen: boolean; item?: IResponsible; isFirst?: boolean }> {
        return this.orgTreeService.toggleDrawer;
    }

    get selectedItem(): IResponsible {
        return this.orgTreeService.toggleDrawer.value?.item;
    }

    toggleDrawer(item?: IResponsible, force?: boolean) {
        this.orgTreeService.toggleDrawer.next({ isOpen: !!item, item, force });
    }

    get currentConfiguration(): IOrganisationConfiguration {
        return this.orgTreeService.currentConfiguration;
    }

    set currentConfiguration(val: IOrganisationConfiguration) {
        this.orgTreeService.currentConfiguration = val;
    }

    get rootNode(): IListView<IResponsible> & Partial<IResponsible> {
        return this.orgTreeService.rootNode;
    }

    set rootNode(value: IListView<IResponsible> & Partial<IResponsible>) {
        this.orgTreeService.rootNode = value;
    }

    get activeQuery(): IQueryOrganisation {
        return this.orgTreeService.activeQuery;
    }

    get orgCode(): string {
        return this.activeQuery?.orgCode;
    }

    set orgCode(val: string) {
        this.orgTreeService.activeQuery.orgCode = val;
    }

    get responsibleType(): ResponsibleType {
        return this.activeQuery?.responsibleType;
    }

    set responsibleType(val: ResponsibleType) {
        this.orgTreeService.activeQuery.responsibleType = val;
    }

    get isOrgNode(): boolean {
        return !!this.orgCode;
    }

    get shouldReloadOrgTree(): EventEmitter<Array<string> | void> {
        return this.orgTreeService.reloadOrgTree;
    }

    reloadOrgTree = (publishedOrgCodes?: string[]) => this.orgTreeService.reloadOrgTree.emit(publishedOrgCodes);

    get isStartReportingAble(): boolean {
        return this.rootNode?.children?.some(e => e.lastActiveReportingPeriod !== this.activePeriod);
    }

    get isUnPublishAble(): boolean {
        return this.rootNode?.children?.some(e => e.lastActiveReportingPeriod === this.activePeriod);
    }

    get communicationConfigurations(): ICommunicationConfiguration {
        return this.currentConfiguration?.communication;
    }

    get isCommunicationEnabled(): boolean {
        return this.communicationConfigurations?.isEnabled;
    }

    get freezeDateReached(): boolean {
        return (
            currentDate.getFullYear() ===
                new Date(
                    this.currentConfiguration?.freezeDate || this.currentConfiguration?.maxFreezeDate
                ).getFullYear() &&
            moment(currentDate.atMidnight()).unix() >=
                moment(
                    new Date(
                        this.currentConfiguration?.freezeDate || this.currentConfiguration?.maxFreezeDate
                    ).atMidnight()
                ).unix()
        );
    }

    get isCascadingAllowed(): boolean {
        return (
            this.isEditActive &&
            (this.isCascadedReportingActive || (!!this.rootNode?.responsibleType && !this.rootNode?.orgCode)) &&
            (!!this.rootNode?.orgCode || !!this.rootNode?.responsibleType)
        );
    }
}
